import React, { useState, useEffect } from 'react';
import CreatableSelect from 'react-select/creatable';
import { companyGroupService, companyService } from '../../../api/apiService';
import '../../../App.css';
import edit from '../../../images/edit.svg';
import deleteIcon from '../../../images/delete.svg';

const CompanyGroupManagement = () => {
  const [groups, setGroups] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingCompany, setIsCreatingCompany] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentGroup, setCurrentGroup] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  // Form fields for group creation/editing
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [search, setSearch] = useState('');

  // Company creation form fields
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');

  // Custom styles for react-select
  const customStyles = {
    control: (base) => ({
      ...base,
      minWidth: "300px",
      borderColor: "black",
      borderWidth: "1px",
      boxShadow: "none",
      margin: "5px",
      borderRadius: "0px",
      color: "black",
      fontWeight: "700",
      height: "auto",
      textTransform: "uppercase",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "black",
      fontWeight: "700",
    }),
    input: (provided) => ({
      ...provided,
      color: "black",
      fontWeight: "700",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "black" : "inherit",
      "&:hover": { backgroundColor: state.isSelected ? "black" : "#f7f7f7" },
    }),
  };

  // Fetch company groups
  const fetchGroups = async () => {
    try {
      setIsLoading(true);
      const response = await companyGroupService.getAllGroups();
      setGroups(response.data.data.groups || []);
    } catch (error) {
      console.error('Error fetching company groups:', error);
      alert('Error fetching company groups');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch companies for the select dropdown
  const fetchCompanies = async () => {
    try {
      const response = await companyService.getAllCompanies(1, 1000);
      const companiesData = response?.data?.companies || response?.data || [];
      
      const formattedCompanies = companiesData
        .filter(company => company && company.name) // Filter out companies without names
        .map(company => ({
          value: company.id,
          label: company.name
        }))
        .sort((a, b) => {
          // Add null checks for sorting
          const labelA = a.label || '';
          const labelB = b.label || '';
          return labelA.localeCompare(labelB);
        });
      
      setCompanies(formattedCompanies);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  // Initialize data
  useEffect(() => {
    fetchGroups();
    fetchCompanies();
  }, []);

  // Filter groups based on search
  const filteredGroups = groups.filter(group =>
    group.name.toLowerCase().includes(search.toLowerCase()) ||
    group.description?.toLowerCase().includes(search.toLowerCase())
  );

  // Reset group form
  const resetGroupForm = () => {
    setGroupName('');
    setGroupDescription('');
    setSelectedCompanies([]);
    setCurrentGroup(null);
    setIsEditing(false);
    setShowCreateForm(false);
  };

  // Reset company form
  const resetCompanyForm = () => {
    setCompanyName('');
    setCompanyEmail('');
    setCompanyAddress('');
    setCompanyPhone('');
    setShowCompanyForm(false);
  };

  // Handle create company from dropdown
  const handleCreateCompanyFromDropdown = async (inputValue) => {
    try {
      setIsCreatingCompany(true);
      
      if (!inputValue || !inputValue.trim()) {
        alert('Company name cannot be empty');
        return;
      }
      
      const companyData = {
        name: inputValue.trim(),
        email: '',
        address: '',
        phone_number: ''
      };

      const response = await companyService.createCompany(companyData);
      
      // Handle different response formats
      let newCompany;
      if (response.data?.company) {
        newCompany = response.data.company;
      } else if (response.data?.id) {
        newCompany = response.data;
      } else {
        throw new Error('Invalid response format from server');
      }
      
      // Ensure we have required fields
      if (!newCompany.id || !newCompany.name) {
        throw new Error('Created company is missing required fields');
      }
      
      // Create the option object for the select
      const newOption = {
        value: newCompany.id,
        label: newCompany.name
      };
      
      // Add to companies list with proper sorting
      setCompanies(prev => {
        const updated = [...prev, newOption];
        return updated.sort((a, b) => {
          const labelA = a.label || '';
          const labelB = b.label || '';
          return labelA.localeCompare(labelB);
        });
      });
      
      // Add to selected companies
      setSelectedCompanies(prev => [...prev, newOption]);
      
      console.log(`Company "${inputValue}" created successfully and added to selection`);
      
    } catch (error) {
      console.error('Error creating company from dropdown:', error);
      alert('Error creating company: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsCreatingCompany(false);
    }
  };

  // Handle create company
  const handleCreateCompany = async (e) => {
    e.preventDefault();
    try {
      if (!companyName.trim()) {
        alert('Company name is required');
        return;
      }

      const companyData = {
        name: companyName.trim(),
        email: companyEmail.trim(),
        address: companyAddress.trim(),
        phone_number: companyPhone.trim()
      };

      await companyService.createCompany(companyData);
      alert('Company created successfully');
      
      // Reset form
      resetCompanyForm();
      
      // Refresh companies list
      await fetchCompanies();
    } catch (error) {
      console.error('Error creating company:', error);
      alert('Error creating company: ' + (error.response?.data?.message || error.message));
    }
  };

  // Handle create group
  const handleCreateGroup = async () => {
    try {
      if (!groupName.trim()) {
        alert('Group name is required');
        return;
      }

      if (selectedCompanies.length === 0) {
        alert('At least one company must be selected');
        return;
      }

      const groupData = {
        name: groupName.trim(),
        description: groupDescription.trim(),
        companies: selectedCompanies
      };

      await companyGroupService.createGroup(groupData);
      alert('Company group created successfully');
      resetGroupForm();
      fetchGroups();
    } catch (error) {
      console.error('Error creating company group:', error);
      alert('Error creating company group');
    }
  };

  // Handle edit group
  const handleEditGroup = (group) => {
    setCurrentGroup(group);
    setGroupName(group.name);
    setGroupDescription(group.description || '');
    
    // Format companies for the select component
    const formattedCompanies = group.companies.map(company => ({
      value: company.value,
      label: company.label
    }));
    setSelectedCompanies(formattedCompanies);
    
    setIsEditing(true);
    setShowCreateForm(true);
  };

  // Handle update group
  const handleUpdateGroup = async () => {
    try {
      if (!groupName.trim()) {
        alert('Group name is required');
        return;
      }

      if (selectedCompanies.length === 0) {
        alert('At least one company must be selected');
        return;
      }

      const groupData = {
        name: groupName.trim(),
        description: groupDescription.trim(),
        companies: selectedCompanies
      };

      await companyGroupService.updateGroup(currentGroup.id, groupData);
      alert('Company group updated successfully');
      resetGroupForm();
      fetchGroups();
    } catch (error) {
      console.error('Error updating company group:', error);
      alert('Error updating company group');
    }
  };

  // Handle delete group
  const handleDeleteGroup = async (groupId, groupName) => {
    if (window.confirm(`Are you sure you want to delete "${groupName}"? This action cannot be undone.`)) {
      try {
        await companyGroupService.deleteGroup(groupId);
        alert('Company group deleted successfully');
        fetchGroups();
      } catch (error) {
        console.error('Error deleting company group:', error);
        alert('Error deleting company group');
      }
    }
  };

  return (
    <div className="content-wrapper page">
      <div className="content-wrapper">
        <h1>Company Group Management</h1>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '40px', marginBottom: '20px' }}>
          <input
            type="text"
            placeholder="Search groups..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="inputtext search"
            style={{ width: '300px' }}
          />
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              className="big-button"
              onClick={() => setShowCompanyForm(true)}
              style={{ textDecoration: "none", alignSelf: "start" }}
            >
              Add Company
            </button>
            <button
              className="big-button"
              onClick={() => setShowCreateForm(true)}
              style={{ textDecoration: "none", alignSelf: "start" }}
            >
              Create New Group
            </button>
          </div>
        </div>

        {/* Company Creation Form */}
        {showCompanyForm && (
          <div className="create-form" style={{ 
            border: '1px solid #000', 
            padding: '20px', 
            marginBottom: '20px',
          }}>
            <h3>Add New Company</h3>
            
            <form onSubmit={handleCreateCompany}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Company Name *
                  </label>
                  <input
                    type="text"
                    className="inputtext"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Enter company name"
                    required
                    style={{ width: '100%' }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    className="inputtext"
                    value={companyEmail}
                    onChange={(e) => setCompanyEmail(e.target.value)}
                    placeholder="Enter company email"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Address
                  </label>
                  <input
                    type="text"
                    className="inputtext"
                    value={companyAddress}
                    onChange={(e) => setCompanyAddress(e.target.value)}
                    placeholder="Enter company address"
                    style={{ width: '100%' }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Phone Number
                  </label>
                  <input
                    type="text"
                    className="inputtext"
                    value={companyPhone}
                    onChange={(e) => setCompanyPhone(e.target.value)}
                    placeholder="Enter phone number"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="submit"
                  className="button"
                  style={{ backgroundColor: '#000', color: 'white' }}
                >
                  Add Company
                </button>
                <button
                  type="button"
                  className="button"
                  onClick={resetCompanyForm}
                  style={{ backgroundColor: '#000' }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Group Create/Edit Form */}
        {showCreateForm && (
          <div className="create-form" style={{ 
            border: '1px solid #ddd', 
            padding: '20px', 
            marginBottom: '20px',
            backgroundColor: '#f9f9f9'
          }}>
            <h3>{isEditing ? 'Edit Company Group' : 'Create New Company Group'}</h3>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Group Name *
              </label>
              <input
                type="text"
                className="inputtext"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Enter group name"
                style={{ width: '100%', maxWidth: '400px' }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Description
              </label>
              <textarea
                className="inputtext"
                value={groupDescription}
                onChange={(e) => setGroupDescription(e.target.value)}
                placeholder="Enter group description (optional)"
                rows={3}
                style={{ width: '100%', maxWidth: '400px', resize: 'vertical' }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Companies * 
                <small style={{ fontWeight: 'normal', color: '#666', marginLeft: '10px' }}>
                  (Type a company name and press Enter to create a new one if it doesn't exist)
                </small>
              </label>
              <CreatableSelect
                isMulti
                value={selectedCompanies}
                onChange={setSelectedCompanies}
                onCreateOption={handleCreateCompanyFromDropdown}
                options={companies}
                isLoading={isCreatingCompany}
                isDisabled={isCreatingCompany}
                placeholder="Select or create companies for this group"
                formatCreateLabel={(inputValue) => `Create company: "${inputValue}"`}
                noOptionsMessage={() => "No companies found. Type to create a new one."}
                styles={customStyles}
                createOptionPosition="first"
              />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                className="button"
                onClick={isEditing ? handleUpdateGroup : handleCreateGroup}
                disabled={isCreatingCompany}
              >
                {isEditing ? 'Update Group' : 'Create Group'}
              </button>
              <button
                className="button"
                onClick={resetGroupForm}
                style={{ backgroundColor: '#6c757d' }}
                disabled={isCreatingCompany}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Groups List */}
        <div className="content-wrapper content-wrapper-talent-list">
          <div className="talent-list">
            <div className="talent-item-header">
              <div style={{ flexBasis: "200px" }}>Group Name</div>
              <div style={{ flexBasis: "300px" }}>Description</div>
              <div style={{ flexBasis: "400px" }}>Companies</div>
              <div style={{ flexBasis: "150px" }}>Created</div>
              <div style={{ flexBasis: "100px" }}>Actions</div>
            </div>
            
            <div style={{ height: "20px" }}></div>
            
            {isLoading ? (
              <div className="loading">Loading company groups...</div>
            ) : filteredGroups.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                {search ? 'No groups found matching your search.' : 'No company groups created yet.'}
              </div>
            ) : (
              filteredGroups.map((group) => (
                <div key={group.id} className="talent-item">
                  <div className="talent-item-firstline">
                    <div style={{ flexBasis: "200px", fontWeight: 'bold' }}>
                      {group.name}
                    </div>
                    <div style={{ flexBasis: "300px" }}>
                      {group.description || '-'}
                    </div>
                    <div style={{ flexBasis: "400px" }}>
                      <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                        {group.companies && group.companies.length > 0 ? (
                          group.companies.map((company, index) => (
                            <span 
                              key={index}
                              style={{ 
                                display: 'inline-block',
                                margin: '2px',
                                padding: '2px 8px',
                                backgroundColor: '#e9ecef',
                                borderRadius: '3px',
                                fontSize: '0.9em'
                              }}
                            >
                              {company.label}
                            </span>
                          ))
                        ) : (
                          'No companies'
                        )}
                      </div>
                    </div>
                    <div style={{ flexBasis: "150px" }}>
                      {group.created_at ? new Date(group.created_at).toLocaleDateString() : '-'}
                    </div>
                    <div style={{ flexBasis: "100px", display: "flex", gap: "8px" }}>
                      <button 
                        className="edit-button"
                        onClick={() => handleEditGroup(group)}
                        title="Edit group"
                      >
                        <img src={edit} alt="Edit" className="edit-container" />
                      </button>
                      <button 
                        className="edit-button"
                        onClick={() => handleDeleteGroup(group.id, group.name)}
                        title="Delete group"
                        style={{ background: 'transparent' }}
                      >
                        <img 
                          src={deleteIcon} 
                          alt="Delete" 
                          className="edit-container" 
                          style={{ filter: 'invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(82%) contrast(97%)' }}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyGroupManagement; 